import fp from 'lodash/fp';
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Registers } from '@factorin/domain';

import { useSizeContext } from 'Modules/ui';
import { useAction } from 'Modules/core/redux';
import { useTranslation } from 'Modules/core/i18n';
import {
    PaginationHeader,
    PaginationList,
    PaginationParametersControl,
    paginationActions,
    IPaginationParameters,
    usePagination,
} from 'Modules/business/data';

import { ListFilterField } from '../ListFilterField';
import { filterFieldForDomain } from '../../constants';
import { ListItem, DiscountListItem, rowHeights } from './ListItem';
import { TransferButton } from './TransferButton';
import { RegistersListProps, ParameterModel } from './types';
import { ListItemType } from './ListItem/types';
import styles from './index.module.scss';


const useRowHeight = (rowHeightRem: RegistersListProps['rowHeightRem']) => {
    const { rem } = useSizeContext();
    const result = useMemo(
        () => fp.isFunction(rowHeightRem)
            ? fp.pipe(rowHeightRem, fp.multiply(rem))
            : rowHeightRem * rem,
        [rowHeightRem, rem],
    );
    return result;
};

const filterField = filterFieldForDomain.registers;

const useListPagination = (path: string, modelParameters: IPaginationParameters) => {
    const actualParameters = usePagination(path, 'parameters');

    const setPaginationParameters = useAction(
        (parameters: IPaginationParameters) => paginationActions.setParameters(
            path,
            parameters,
        ),
        [path],
    );
    useEffect(
        () => {
            if (!fp.isEqual(fp.omit(`filters.${filterField}`, actualParameters), modelParameters))
                setPaginationParameters(modelParameters);
        },
        [modelParameters, actualParameters, setPaginationParameters],
    );
};

export const RegistersList = React.memo<RegistersListProps>(({
    selectedId,
    basePath,
    model,
    parametersModel,
    onRowSelected,
    deselectRegister,
    onModelChange,
    item: Item = ListItem,
    itemStyler,
    rowHeightRem = rowHeights({}),
}) => {
    const [{ key, placeholder }, setModel] = useState(
        () => fp.flatten(parametersModel)[0],
    );
    const selectModel = useCallback(
        (model: ParameterModel) => {
            if (onModelChange)
                onModelChange(model);

            setModel(model);
        },
        [onModelChange],
    );

    const path = `${basePath}/${key}`;

    useListPagination(path, model[key]);

    const elementRenderer = useCallback(
        (props: Omit<ListItemType, 'itemStyler'>) => (
            <Item
                itemStyler={itemStyler}
                {...props}
            />
        ),
        [Item, itemStyler],
    );

    const onRowSelectedCallback = useCallback(
        (register: Registers.Register) => {
            if (!register && deselectRegister)
                deselectRegister();

            if (register && onRowSelected)
                onRowSelected(register.Uid);
        },
        [deselectRegister, onRowSelected],
    );

    const rowHeight = useRowHeight(rowHeightRem);

    const { t } = useTranslation('registersList');
    const placeholderText: React.ReactNode = placeholder
        ? t(`registersListPlaceholders:${placeholder}`)
        : undefined;

    return (
        <div className={styles.list}>
            <PaginationHeader
                updateAnytime
                path={path}
                title={t('title') as string}
                buttons={TransferButton}
            />
            <PaginationParametersControl
                options={parametersModel}
                value={key}
                onChange={selectModel}
            />
            <ListFilterField
                key={`${path}-${filterField}`}
                path={path}
                field={filterField}
            />
            <PaginationList<Registers.Register>
                className={styles.container}
                path={path}
                selectedId={selectedId}
                onRowSelected={onRowSelectedCallback}
                elementRenderer={elementRenderer}
                rowHeight={rowHeight}
                placeholderText={placeholderText}
            />
        </div>
    );
});

export const RegisterItem = ListItem;
export const DiscountRegisterItem = DiscountListItem;

export { datesRegisterTemplateCodes, rowHeights as registerRowHeights } from './ListItem';
